/**
 * AgenticMasteryHome — /agentic-ai-mastery
 *
 * Landing page for Agentic AI Mastery (Product #1).
 * Fetches curriculum from GET /api/v1/agentic-mastery/curriculum and
 * renders milestones + module cards. Visual language preserved from the
 * original hardcoded implementation.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation, Trans } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LockIcon from "@mui/icons-material/Lock";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TuneIcon from "@mui/icons-material/Tune";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getAgenticCurriculum,
  getMasteryProgress,
  getProjects,
  getCertificate,
  AgenticCurriculum,
  AgenticProgress,
  AgenticProjectSummary,
  AgenticCertificate,
} from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { nextMasteryModule } from "../utils/heroCtas";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// "Why this curriculum" cards — static content, translated via the key-map
// pattern (see ArtifactCard.tsx) since the array itself lives outside any
// single JSX expression.
const WHY_CURRICULUM_ITEM_KEYS = [
  {
    title: "AgenticMasteryHome.whyGroundedTitle",
    body: "AgenticMasteryHome.whyGroundedBody",
  },
  {
    title: "AgenticMasteryHome.whyCrossLinkedTitle",
    body: "AgenticMasteryHome.whyCrossLinkedBody",
  },
  {
    title: "AgenticMasteryHome.whyProductionFirstTitle",
    body: "AgenticMasteryHome.whyProductionFirstBody",
  },
] as const;

// Trust-chip labels under the hero CTA — static content, translated via keys.
const TRUST_CHIP_KEYS = [
  "AgenticMasteryHome.trustChipModule1Free",
  "AgenticMasteryHome.trustChipCrossLinkedPlaybook",
] as const;

/**
 * Derives the curriculum total from the server-authored module study times.
 * A malformed or incomplete response produces no total rather than a stale
 * marketing claim.
 */
export function curriculumHours(curriculum?: Pick<AgenticCurriculum, "milestones">): number | null {
  const modules = curriculum?.milestones.flatMap((milestone) => milestone.modules) ?? [];
  if (modules.length === 0) return null;

  const hours = modules.map((module) => {
    const match = module.studyTime.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?)?\b/i);
    return match ? Number(match[1]) : Number.NaN;
  });

  return hours.every(Number.isFinite)
    ? hours.reduce((total, value) => total + value, 0)
    : null;
}

export default function AgenticMasteryHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation("pages-core");

  const { data: curriculum, isLoading, error } = useQuery<AgenticCurriculum>({
    queryKey: ["agentic-curriculum"],
    queryFn: getAgenticCurriculum,
    retry: false,
  });

  const { data: progress } = useQuery<AgenticProgress>({
    queryKey: ["agentic-progress"],
    queryFn: getMasteryProgress,
    enabled: isAuthenticated,
    retry: false,
  });

  const { data: projectsData } = useQuery<{ projects: AgenticProjectSummary[] }>({
    queryKey: ["agentic-projects"],
    queryFn: getProjects,
    retry: false,
  });

  const { data: certificate } = useQuery<AgenticCertificate>({
    queryKey: ["agentic-certificate"],
    queryFn: getCertificate,
    enabled: isAuthenticated,
    retry: false,
  });

  // All modules in curriculum order, mapped to the shape the hero-CTA helper needs.
  const allModules =
    curriculum?.milestones?.flatMap((m) => m.modules) ?? [];
  const totalCurriculumHours = curriculumHours(curriculum);
  const heroCta = nextMasteryModule(
    allModules.map((m) => ({ slug: m.slug, title: m.title })),
    progress,
    t,
  );

  // Compute overall progress values
  const progressPct =
    progress && progress.counts.total > 0
      ? Math.round((progress.counts.completed / progress.counts.total) * 100)
      : 0;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="flex-start">
            <Chip
              label={t("AgenticMasteryHome.heroChipLabel")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.08em",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h2"
              fontWeight={500}
              sx={{ letterSpacing: "-0.03em", fontSize: { xs: "2.4rem", md: "3.2rem" }, maxWidth: 900 }}
            >
              {curriculum?.title ?? t("AgenticMasteryHome.heroTitleFallback")}
              <br />
              <Box component="span" sx={{ color: theme.palette.primary.main }}>
                {t("AgenticMasteryHome.heroTitleSuffix")}
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, maxWidth: 780, lineHeight: 1.5 }}
            >
              {curriculum?.subtitle ?? t("AgenticMasteryHome.heroSubtitleFallback")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() =>
                  navigate(`/agentic-ai-mastery/module/${heroCta.slug}`)
                }
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  px: 3,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {heroCta.label}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/pricing")}
                sx={{
                  fontWeight: 500,
                  px: 3,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: theme.palette.primary.main,
                  "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                {t("AgenticMasteryHome.seeAllAccessPricingButton")}
              </Button>
            </Stack>
            <Stack direction="row" spacing={3} mt={3} flexWrap="wrap" gap={1}>
              {TRUST_CHIP_KEYS.map((key) => (
                <Stack key={key} direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                  <Typography variant="body2" color="text.secondary">
                    {t(key)}
                  </Typography>
                </Stack>
              ))}
              {totalCurriculumHours !== null && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("AgenticMasteryHome.trustChipCurriculumHours", {
                      hours: totalCurriculumHours,
                    })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Overall progress bar — authenticated users only */}
      {isAuthenticated && progress && (
        <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Typography variant="body2" fontWeight={500} sx={{ color: theme.palette.primary.main, whiteSpace: "nowrap" }}>
                {t("AgenticMasteryHome.overallProgressLabel", {
                  completed: progress.counts.completed,
                  total: progress.counts.total,
                })}
              </Typography>
              <Box flex={1} sx={{ minWidth: 120 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.main, borderRadius: 4 },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {progressPct}%
              </Typography>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Curriculum */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1} mb={5}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("AgenticMasteryHome.curriculumEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("AgenticMasteryHome.curriculumTitle")}
          </Typography>
        </Stack>

        {isLoading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress sx={{ color: theme.palette.primary.main }} />
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
            {t("AgenticMasteryHome.curriculumLoadErrorMessage")}
          </Alert>
        )}

        {curriculum && (
          <Stack spacing={6}>
            {curriculum.milestones.map((milestone) => (
              <Box key={milestone.id}>
                {/* Milestone header */}
                <Stack spacing={0.5} mb={3}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography
                      variant="overline"
                      sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
                    >
                      {t("AgenticMasteryHome.milestoneLabel", { number: milestone.number })}
                    </Typography>
                    {isAuthenticated && progress?.byMilestone?.[milestone.id] && (
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>
                        {t("AgenticMasteryHome.milestoneProgressLabel", {
                          completed: progress.byMilestone[milestone.id].completed,
                          total: progress.byMilestone[milestone.id].total,
                        })}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em" }}>
                    {milestone.title}
                  </Typography>
                  {milestone.summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                      {milestone.summary}
                    </Typography>
                  )}
                </Stack>

                {/* Module cards */}
                <Stack spacing={2}>
                  {milestone.modules.map((m) => (
                    <Paper
                      key={m.slug}
                      elevation={0}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        p: 3,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                          transform: "translateY(-2px)",
                        },
                      }}
                      onClick={() => navigate(`/agentic-ai-mastery/module/${m.slug}`)}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", md: "center" }}
                      >
                        {/* Module number badge — shows checkmark when completed */}
                        {(() => {
                          const isCompleted =
                            isAuthenticated &&
                            (m.completed ??
                              progress?.completedSlugs?.includes(m.slug));
                          return (
                            <Tooltip
                              title={isCompleted ? t("AgenticMasteryHome.moduleCompletedTooltip") : ""}
                              placement="top"
                              disableHoverListener={!isCompleted}
                            >
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 2,
                                  bgcolor: isCompleted
                                    ? alpha(theme.palette.success.main, 0.12)
                                    : alpha(theme.palette.primary.main, 0.12),
                                  color: isCompleted ? theme.palette.success.main : theme.palette.primary.main,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  fontWeight: 500,
                                  fontSize: "1.5rem",
                                  letterSpacing: "-0.02em",
                                  transition: "all 0.2s",
                                }}
                              >
                                {isCompleted ? (
                                  <CheckCircleIcon sx={{ fontSize: 28, color: theme.palette.success.main }} />
                                ) : (
                                  m.number
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })()}

                        <Box flex={1} minWidth={0}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mb={0.5}
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            <Typography variant="h6" fontWeight={500}>
                              {m.title}
                            </Typography>
                            {m.locked ? (
                              <Chip
                                icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />}
                                label={t("AgenticMasteryHome.allAccessChipLabel")}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                                  color: theme.palette.primary.main,
                                  fontWeight: 500,
                                  fontSize: "0.68rem",
                                  height: 20,
                                }}
                              />
                            ) : (
                              <Chip
                                label={t("AgenticMasteryHome.freeChipLabel")}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.success.main, 0.12),
                                  color: theme.palette.success.dark,
                                  fontWeight: 500,
                                  fontSize: "0.68rem",
                                  height: 20,
                                }}
                              />
                            )}
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1.5, lineHeight: 1.55 }}
                          >
                            {m.tagline}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ScheduleIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                            <Typography variant="caption" color="text.secondary">
                              {m.studyTime}
                            </Typography>
                          </Stack>
                        </Box>

                        <ArrowForwardIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        {/* Production Projects */}
        <Box mt={{ xs: 6, md: 8 }}>
          <Stack spacing={1} mb={4}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              {t("AgenticMasteryHome.productionProjectsEyebrow")}
            </Typography>
            <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
              {t("AgenticMasteryHome.productionProjectsTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
              {t("AgenticMasteryHome.productionProjectsIntro")}
            </Typography>
          </Stack>

          {projectsData && projectsData.projects.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
              }}
            >
              {projectsData.projects.map((proj) => (
                <Paper
                  key={proj.id}
                  elevation={0}
                  onClick={() =>
                    !proj.locked && navigate(`/agentic-ai-mastery/project/${proj.id}`)
                  }
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    p: 3,
                    cursor: proj.locked ? "default" : "pointer",
                    transition: "all 0.2s",
                    position: "relative",
                    ...(proj.passed && {
                      borderColor: alpha(theme.palette.success.main, 0.4),
                      bgcolor: alpha(theme.palette.success.main, 0.02),
                    }),
                    ...(!proj.locked && !proj.passed && {
                      "&:hover": {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                        transform: "translateY(-2px)",
                      },
                    }),
                  }}
                >
                  {/* Capstone badge */}
                  {proj.isCapstone && (
                    <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                      <Chip
                        label={t("AgenticMasteryHome.capstoneChipLabel")}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.65rem",
                          letterSpacing: "0.05em",
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          height: 20,
                        }}
                      />
                    </Box>
                  )}

                  <Stack spacing={1.5}>
                    <Typography variant="overline" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                      {proj.milestone}
                    </Typography>
                    <Typography variant="h6" fontWeight={500} sx={{ pr: proj.isCapstone ? 7 : 0 }}>
                      {proj.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                      {proj.tagline}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5} mt={0.5}>
                      {proj.locked ? (
                        <Chip
                          icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />}
                          label={t("AgenticMasteryHome.allAccessChipLabel")}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            fontSize: "0.68rem",
                            height: 20,
                          }}
                        />
                      ) : proj.passed ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: "0.8rem !important" }} />}
                          label={t("AgenticMasteryHome.passedScoreChipLabel", {
                            score: Math.round((proj.bestScore ?? 0) * 100),
                          })}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.15),
                            color: theme.palette.success.dark,
                            fontWeight: 500,
                            fontSize: "0.68rem",
                            height: 20,
                          }}
                        />
                      ) : proj.bestScore !== undefined ? (
                        <Chip
                          label={t("AgenticMasteryHome.bestScoreChipLabel", {
                            score: Math.round(proj.bestScore * 100),
                          })}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            fontSize: "0.68rem",
                            height: 20,
                          }}
                        />
                      ) : null}
                    </Stack>

                    {!proj.locked && (
                      <Button
                        size="small"
                        variant={proj.passed ? "outlined" : "contained"}
                        endIcon={<ArrowForwardIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agentic-ai-mastery/project/${proj.id}`);
                        }}
                        sx={{
                          alignSelf: "flex-start",
                          mt: 0.5,
                          fontWeight: 500,
                          textTransform: "none",
                          ...(proj.passed
                            ? {
                                borderColor: alpha(theme.palette.success.main, 0.5),
                                color: theme.palette.success.dark,
                                "&:hover": { borderColor: theme.palette.success.main, bgcolor: alpha(theme.palette.success.main, 0.04) },
                              }
                            : {
                                bgcolor: theme.palette.primary.main,
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                              }),
                        }}
                      >
                        {proj.passed
                          ? t("AgenticMasteryHome.reviewButtonLabel")
                          : t("AgenticMasteryHome.startProjectButtonLabel")}
                      </Button>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("AgenticMasteryHome.noProjectsYetMessage")}
            </Typography>
          )}
        </Box>

        {/* Certificate banner — authenticated users only */}
        {isAuthenticated && certificate && (
          <Box mt={{ xs: 6, md: 8 }}>
            {certificate.eligible ? (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                  borderRadius: 3,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={3}
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <EmojiEventsIcon sx={{ fontSize: 48, color: theme.palette.success.main, flexShrink: 0 }} />
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={500} gutterBottom sx={{ color: theme.palette.success.dark }}>
                      {t("AgenticMasteryHome.certificateEarnedTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("AgenticMasteryHome.certificateEarnedBody")}
                    </Typography>
                    {certificate.certificateId && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 1, color: "text.disabled", fontFamily: "monospace" }}
                      >
                        {t("AgenticMasteryHome.certificateIdLabel", {
                          certificateId: certificate.certificateId,
                        })}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<WorkspacePremiumIcon />}
                    sx={{
                      bgcolor: theme.palette.success.main,
                      fontWeight: 500,
                      flexShrink: 0,
                      "&:hover": { bgcolor: theme.palette.success.dark },
                    }}
                  >
                    {t("AgenticMasteryHome.viewCertificateButton")}
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={3}
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <WorkspacePremiumIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5), flexShrink: 0 }} />
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={500} gutterBottom>
                      {t("AgenticMasteryHome.certificateNotEarnedTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t("AgenticMasteryHome.certificateNotEarnedBody")}
                    </Typography>
                    <Stack direction="row" spacing={3} flexWrap="wrap" gap={1}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {t("AgenticMasteryHome.certificateModulesLabel")}
                        </Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ color: theme.palette.primary.main }}>
                          {t("AgenticMasteryHome.certificateModulesProgress", {
                            complete: certificate.requirements.modulesComplete,
                            total: certificate.requirements.modulesTotal,
                          })}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {t("AgenticMasteryHome.certificateCapstoneLabel")}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {certificate.requirements.capstonePassed ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                          ) : (
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                              }}
                            />
                          )}
                          <Typography variant="body2" fontWeight={500} sx={{ color: certificate.requirements.capstonePassed ? theme.palette.success.dark : theme.palette.primary.main }}>
                            {certificate.requirements.capstonePassed
                              ? t("AgenticMasteryHome.capstonePassedLabel")
                              : t("AgenticMasteryHome.capstoneNotYetLabel")}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>
        )}

        {/* Why it's different */}
        <Box mt={{ xs: 6, md: 8 }}>
          <Stack spacing={1} mb={4}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              {t("AgenticMasteryHome.whyCurriculumEyebrow")}
            </Typography>
            <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
              {t("AgenticMasteryHome.whyCurriculumTitle")}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 3,
            }}
          >
            {WHY_CURRICULUM_ITEM_KEYS.map((item) => (
              <Paper
                key={item.title}
                elevation={0}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Typography variant="h6" fontWeight={500} gutterBottom>
                  {t(item.title)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {t(item.body)}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Companion course — Agentic Ops cross-link (Mastery → Ops) */}
        <Box mt={{ xs: 6, md: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              gap: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              borderRadius: 3,
            }}
          >
            <TuneIcon sx={{ fontSize: 44, color: theme.palette.primary.main, flexShrink: 0 }} />
            <Box flex={1}>
              <Typography
                variant="overline"
                sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {t("AgenticMasteryHome.companionEyebrow")}
              </Typography>
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t("AgenticMasteryHome.companionTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
                <Trans
                  t={t}
                  i18nKey="AgenticMasteryHome.companionBody"
                  components={{ strong: <strong /> }}
                />
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/agentic-ops")}
              sx={{
                flexShrink: 0,
                fontWeight: 500,
                borderColor: alpha(theme.palette.primary.main, 0.5),
                color: theme.palette.primary.main,
                "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              {t("AgenticMasteryHome.companionButton")}
            </Button>
          </Paper>
        </Box>
      </Container>

      {/* Final CTA — fresh-user conversion pitch; hidden once enrolled & in-progress */}
      {(!progress || progress.counts.completed === 0) && (
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            p: { xs: 4, md: 6 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 3,
          }}
        >
          <PsychologyIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h4" fontWeight={500} gutterBottom sx={{ letterSpacing: "-0.02em" }}>
            {t("AgenticMasteryHome.finalCtaTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: "auto", mb: 4 }}>
            {t("AgenticMasteryHome.finalCtaBody")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() =>
              navigate(`/agentic-ai-mastery/module/${heroCta.slug}`)
            }
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {t("AgenticMasteryHome.finalCtaButton")}
          </Button>
        </Paper>
      </Container>
      )}
    </Box>
  );
}
